import {
  Button,
  Tooltip,
  type ButtonProps,
  type TooltipProps,
} from "@mantine/core";

interface TooltipButtonProps extends ButtonProps {
  /**
   * Tooltip text. When falsy, no tooltip is rendered (a plain button). Pass the
   * reason a button is disabled here so the user can discover *why*.
   */
  tooltip?: string;
  tooltipProps?: Partial<Omit<TooltipProps, "label" | "children">>;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  type?: "button" | "submit" | "reset";
}

/**
 * A Mantine Button that shows its Tooltip even when disabled.
 *
 * A natively-`disabled` button suppresses pointer events, so a `<Tooltip>`
 * wrapped directly around it never fires on hover — the explanation for *why*
 * the button is disabled is exactly when the user most wants it, and it never
 * appears (the long-standing Mantine caveat). This wrapper keeps the real
 * `disabled` attribute (so it stays inert + `toBeDisabled()` in tests) but, when
 * disabled, nests the button in a hoverable `<span>` and sets `pointer-events:
 * none` on the button so hover events fall through to the span the Tooltip
 * listens on.
 *
 * Prefer this over `<Tooltip><Button disabled/></Tooltip>` anywhere a disabled
 * button needs to explain itself.
 */
export function TooltipButton({
  tooltip,
  tooltipProps,
  disabled,
  style,
  ...buttonProps
}: TooltipButtonProps) {
  if (!tooltip) {
    return <Button disabled={disabled} style={style} {...buttonProps} />;
  }

  if (!disabled) {
    return (
      <Tooltip label={tooltip} {...tooltipProps}>
        <Button style={style} {...buttonProps} />
      </Tooltip>
    );
  }

  return (
    <Tooltip label={tooltip} {...tooltipProps}>
      <span style={{ display: "inline-flex", cursor: "not-allowed" }}>
        <Button
          disabled
          aria-disabled
          style={{ ...style, pointerEvents: "none" }}
          {...buttonProps}
        />
      </span>
    </Tooltip>
  );
}
